export type WandPoint = { x: number; y: number };

export type WandBox = { left: number; top: number; right: number; bottom: number };

export type WandDisk = {
  shape: 'disk';
  x: number;
  y: number;
  r: number;
  prev?: WandPoint;
  next?: WandPoint;
};

export type WandHit = { shape: 'box'; box: WandBox } | WandDisk;

export type WandKind = 'metric' | 'comparison' | 'table-cell' | 'driver' | 'text' | 'chart-mark' | 'chart';

export type WandElement = {
  cardId: string;
  cardTitle: string;
  kind: WandKind;
  label: string;
  value?: string;
  hit: WandHit;
  node?: HTMLElement;
};

export type WandFrame = WandBox & { id: string; title: string };

export type WandDomTarget = {
  kind: Exclude<WandKind, 'chart-mark' | 'chart'>;
  label: string;
  value?: string;
};

export const WAND_TARGET_ATTR = 'data-lens-wand';
export const STROKE_POINT_SPACING = 3;
export const MIN_STROKE_LENGTH = 36;
export const MIN_RING_AREA = 400;
export const WAND_QUESTION_LIMIT = 6;
export const WAND_CONTEXT_LIMIT = 40;

export function wandTargetAttribute(target: WandDomTarget): string {
  return JSON.stringify({ kind: target.kind, label: target.label, value: target.value ?? '' });
}

export function parseWandTarget(raw: string | null): WandDomTarget | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<WandDomTarget>;
    if (typeof parsed.kind !== 'string' || typeof parsed.label !== 'string') {
      return null;
    }
    if (
      parsed.kind !== 'metric' &&
      parsed.kind !== 'comparison' &&
      parsed.kind !== 'table-cell' &&
      parsed.kind !== 'driver' &&
      parsed.kind !== 'text'
    ) {
      return null;
    }
    return { kind: parsed.kind, label: parsed.label, value: typeof parsed.value === 'string' ? parsed.value : '' };
  } catch {
    return null;
  }
}

export function pathLength(points: ReadonlyArray<WandPoint>): number {
  let length = 0;
  for (let index = 1; index < points.length; index += 1) {
    length += Math.hypot(points[index].x - points[index - 1].x, points[index].y - points[index - 1].y);
  }
  return length;
}

export function closeRing(points: ReadonlyArray<WandPoint>): Array<WandPoint> {
  if (points.length === 0) {
    return [];
  }
  const first = points[0];
  const last = points[points.length - 1];
  if (Math.hypot(first.x - last.x, first.y - last.y) < 1) {
    return [...points];
  }
  return [...points, first];
}

export function ringArea(ring: ReadonlyArray<WandPoint>): number {
  let sum = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const current = ring[index];
    const next = ring[index + 1];
    sum += current.x * next.y - next.x * current.y;
  }
  return Math.abs(sum) / 2;
}

export function strokeUsable(points: ReadonlyArray<WandPoint>): boolean {
  if (points.length < 3 || pathLength(points) < MIN_STROKE_LENGTH) {
    return false;
  }
  return ringArea(closeRing(points)) >= MIN_RING_AREA;
}

// parked: restore lasso
export function appendStrokePoint(points: ReadonlyArray<WandPoint>, point: WandPoint): Array<WandPoint> {
  const last = points[points.length - 1];
  if ((last && Math.hypot(point.x - last.x, point.y - last.y) < STROKE_POINT_SPACING) || points.length >= 600) {
    return points as Array<WandPoint>;
  }
  return [...points, point];
}

export function axisAlignedRectangle(anchor: WandPoint, corner: WandPoint): Array<WandPoint> {
  const left = Math.min(anchor.x, corner.x);
  const top = Math.min(anchor.y, corner.y);
  const right = Math.max(anchor.x, corner.x);
  const bottom = Math.max(anchor.y, corner.y);
  return [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ];
}

export function ringBounds(ring: ReadonlyArray<WandPoint>): WandBox {
  const xs = ring.map((point) => point.x);
  const ys = ring.map((point) => point.y);
  return {
    left: Math.min(...xs),
    top: Math.min(...ys),
    right: Math.max(...xs),
    bottom: Math.max(...ys),
  };
}

export function pointInRing(point: WandPoint, ring: ReadonlyArray<WandPoint>): boolean {
  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
    const current = ring[index];
    const prior = ring[previous];
    if (current.y === prior.y && current.x === prior.x) {
      continue;
    }
    const crosses = current.y > point.y !== prior.y > point.y;
    if (!crosses) {
      continue;
    }
    const xAt = ((prior.x - current.x) * (point.y - current.y)) / (prior.y - current.y) + current.x;
    if (point.x < xAt) {
      inside = !inside;
    }
  }
  return inside;
}

function distanceToSegment(point: WandPoint, start: WandPoint, end: WandPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
}

function segmentsCross(a: WandPoint, b: WandPoint, c: WandPoint, d: WandPoint): boolean {
  const cross = (p: WandPoint, q: WandPoint, r: WandPoint) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const ab = cross(a, b, c);
  const ab2 = cross(a, b, d);
  const cd = cross(c, d, a);
  const cd2 = cross(c, d, b);
  if (ab === 0 && ab2 === 0 && cd === 0 && cd2 === 0) {
    return false;
  }
  return ab * ab2 <= 0 && cd * cd2 <= 0;
}

export function diskMeetsRing(x: number, y: number, radius: number, ring: ReadonlyArray<WandPoint>): boolean {
  const center = { x, y };
  if (pointInRing(center, ring)) {
    return true;
  }
  for (let index = 0; index < ring.length - 1; index += 1) {
    if (distanceToSegment(center, ring[index], ring[index + 1]) <= radius) {
      return true;
    }
  }
  return false;
}

export function boxMeetsRing(box: WandBox, ring: ReadonlyArray<WandPoint>): boolean {
  const corners = [
    { x: box.left, y: box.top },
    { x: box.right, y: box.top },
    { x: box.right, y: box.bottom },
    { x: box.left, y: box.bottom },
  ];
  if (corners.some((corner) => pointInRing(corner, ring))) {
    return true;
  }
  if (
    ring.some((point) => point.x >= box.left && point.x <= box.right && point.y >= box.top && point.y <= box.bottom)
  ) {
    return true;
  }
  const edges: Array<[WandPoint, WandPoint]> = [
    [corners[0], corners[1]],
    [corners[1], corners[2]],
    [corners[2], corners[3]],
    [corners[3], corners[0]],
  ];
  for (let index = 0; index < ring.length - 1; index += 1) {
    if (edges.some(([start, end]) => segmentsCross(start, end, ring[index], ring[index + 1]))) {
      return true;
    }
  }
  return false;
}

function segmentMeetsRing(start: WandPoint, end: WandPoint, radius: number, ring: ReadonlyArray<WandPoint>): boolean {
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  const steps = Math.min(32, Math.max(1, Math.ceil(length / 12)));
  for (let step = 0; step <= steps; step += 1) {
    const t = step / steps;
    if (diskMeetsRing(start.x + (end.x - start.x) * t, start.y + (end.y - start.y) * t, radius, ring)) {
      return true;
    }
  }
  for (let index = 0; index < ring.length - 1; index += 1) {
    if (segmentsCross(start, end, ring[index], ring[index + 1])) {
      return true;
    }
  }
  return false;
}

export function elementMeetsRing(element: WandElement, ring: ReadonlyArray<WandPoint>): boolean {
  if (element.hit.shape === 'box') {
    return boxMeetsRing(element.hit.box, ring);
  }
  if (diskMeetsRing(element.hit.x, element.hit.y, element.hit.r, ring)) {
    return true;
  }
  if (element.hit.prev && segmentMeetsRing(element.hit.prev, element.hit, element.hit.r, ring)) {
    return true;
  }
  if (element.hit.next && segmentMeetsRing(element.hit, element.hit.next, element.hit.r, ring)) {
    return true;
  }
  return false;
}

function hitTop(element: WandElement): number {
  return element.hit.shape === 'box' ? element.hit.box.top : element.hit.y;
}

function hitLeft(element: WandElement): number {
  return element.hit.shape === 'box' ? element.hit.box.left : element.hit.x;
}

export function sortWandElements(elements: ReadonlyArray<WandElement>): Array<WandElement> {
  return [...elements].sort((left, right) => hitTop(left) - hitTop(right) || hitLeft(left) - hitLeft(right));
}

export function resolveWandSelection(
  frames: ReadonlyArray<WandFrame>,
  elements: ReadonlyArray<WandElement>,
  ring: ReadonlyArray<WandPoint>,
): { bounds: WandBox; cards: Array<{ id: string; title: string }>; elements: Array<WandElement> } {
  const closed = closeRing(ring);
  return {
    bounds: ringBounds(closed),
    cards: frames
      .filter((frame) => boxMeetsRing(frame, closed))
      .sort((left, right) => left.top - right.top || left.left - right.left)
      .map(({ id, title }) => ({ id, title })),
    elements: sortWandElements(elements.filter((element) => elementMeetsRing(element, closed))),
  };
}

function clipText(value: string, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  return `${trimmed.slice(0, max - 1)}…`;
}

export function wandQuestion(): string {
  return 'I selected a region on this Lens with the magic wand. What is this, and any quick commercial read?';
}

function epochMillis(token: string): number | null {
  if (/^\d{13}$/.test(token)) {
    return Number(token);
  }
  if (/^\d{10}$/.test(token)) {
    return Number(token) * 1000;
  }
  return null;
}

function markIdentity(label: string): { name: string; category: string; at: number | null } {
  const parts = label
    .split(' · ')
    .map((part) => part.trim())
    .filter(Boolean);
  const tail = parts[parts.length - 1] ?? '';
  const at = epochMillis(tail);
  if (at !== null && parts.length > 1) {
    return { name: parts.slice(0, -1).join(' · '), category: '', at };
  }
  if (parts.length > 1) {
    return { name: parts.slice(0, -1).join(' · '), category: tail, at: null };
  }
  return { name: label.trim(), category: '', at: null };
}

function grainWord(epochs: ReadonlyArray<number>): string {
  if (epochs.length < 2) {
    return '';
  }
  const sorted = [...epochs].sort((left, right) => left - right);
  const gaps = sorted.slice(1).map((time, index) => time - sorted[index]);
  gaps.sort((left, right) => left - right);
  const mid = gaps[Math.floor(gaps.length / 2)];
  const day = 86_400_000;
  if (mid <= day * 1.5) {
    return 'daily';
  }
  if (mid <= day * 10) {
    return 'weekly';
  }
  if (mid <= day * 45) {
    return 'monthly';
  }
  return '';
}

function dateSpan(epochs: ReadonlyArray<number>): string {
  const sorted = [...epochs].sort((left, right) => left - right);
  const format = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const start = format.format(new Date(sorted[0]));
  const end = format.format(new Date(sorted[sorted.length - 1]));
  return start === end ? start : `${start}–${end}`;
}

function listed(names: ReadonlyArray<string>): string {
  const unique = [...new Set(names.map((name) => name.trim()).filter(Boolean))];
  const shown = unique.slice(0, WAND_QUESTION_LIMIT);
  const more = unique.length - shown.length;
  const list = more > 0 ? `${shown.join(', ')}, and ${more} more` : shown.join(', ');
  return list;
}

function commercialLines(elements: ReadonlyArray<WandElement>): Array<string> {
  const lines: Array<string> = [];
  const seen = new Set<string>();
  const push = (line: string) => {
    if (seen.has(line)) {
      return;
    }
    seen.add(line);
    lines.push(line);
  };
  const groups = new Map<string, { title: string; name: string; epochs: Array<number>; categories: Array<string> }>();
  elements.forEach((element) => {
    if (element.kind !== 'chart-mark' && element.kind !== 'chart') {
      return;
    }
    const identity = markIdentity(element.kind === 'chart' ? element.cardTitle : element.label);
    const name = identity.name && identity.name !== element.cardTitle ? identity.name : '';
    const key = `${element.cardId}\0${name}`;
    const group = groups.get(key) ?? { title: element.cardTitle, name, epochs: [], categories: [] };
    if (identity.at !== null) {
      group.epochs.push(identity.at);
    } else if (identity.category) {
      group.categories.push(identity.category);
    }
    groups.set(key, group);
  });
  groups.forEach((group) => {
    const head = group.name ? `${group.title} — ${group.name}` : group.title;
    const time = group.epochs.length
      ? [grainWord(group.epochs), group.epochs.length > 1 ? dateSpan(group.epochs) : 'this Lens period']
          .filter(Boolean)
          .join(', ')
      : '';
    const names = listed(group.categories);
    if (time) {
      push(`${head} (${time})`);
      return;
    }
    push(names ? `${head}: ${names}` : head);
  });
  elements.forEach((element) => {
    if (element.kind === 'chart-mark' || element.kind === 'chart') {
      return;
    }
    if (element.kind === 'text') {
      push(`${element.cardTitle} — commentary`);
      return;
    }
    const figure = element.value && !epochMillis(element.value.trim()) ? clipText(element.value, 80) : '';
    if (element.kind === 'metric') {
      push(figure ? `${element.cardTitle} — ${figure}` : element.cardTitle);
      return;
    }
    const label = element.label && element.label !== element.cardTitle ? element.label : '';
    if (figure && label) {
      push(`${element.cardTitle} — ${label}: ${figure}`);
      return;
    }
    push(figure ? `${element.cardTitle} — ${figure}` : label ? `${element.cardTitle} — ${label}` : element.cardTitle);
  });
  return lines.slice(0, WAND_CONTEXT_LIMIT);
}

export function wandContextText(input: {
  bounds: WandBox;
  cards: ReadonlyArray<{ id: string; title: string }>;
  elements: ReadonlyArray<WandElement>;
  image: boolean;
}): string {
  const selected = commercialLines(input.elements);
  const lines = [
    'Magic wand selection. This rectangle is the subject of this turn and overrides any selected-card subject. The reader message does not list these facts. Say what the selection is in commercial language, using the crop. Do not recite timestamps, epoch numbers, or point-by-point series. Do not treat a whole card as selected when only some of its marks are listed.',
    input.cards.length
      ? `Rectangle overlaps: ${input.cards.map((card) => card.title).join('; ')}.`
      : 'Rectangle does not overlap a card frame.',
    selected.length ? `Selected: ${selected.join('; ')}.` : 'No individual marks resolved. Use the crop and the overlapping cards.',
    input.image
      ? 'An image crop of the rectangle is attached to this turn.'
      : 'No image crop could be captured for this region.',
  ];
  return lines.filter(Boolean).join('\n');
}

export function wandDrawStatus(
  selection: { elements: ReadonlyArray<unknown>; cards: ReadonlyArray<unknown> } | null,
  stroking: boolean,
): string {
  if (!stroking || !selection || (selection.elements.length === 0 && selection.cards.length === 0)) {
    return 'Drag a rectangle, then release';
  }
  if (selection.elements.length === 0) {
    return 'Region set — release to ask';
  }
  const count = selection.elements.length;
  return `${count} ${count === 1 ? 'mark' : 'marks'} inside — release to ask`;
}
